import { useState, useId, useEffect, useRef } from "react"
import { Box, Collapse, Group, ThemeIcon, UnstyledButton } from "@mantine/core"
import { ChevronRight } from "@geist-ui/icons"
import "./style.css"
import { useLocation, useNavigate } from "react-router-dom"
import { PATHS } from "src/constants"

interface LinksGroupProps {
  icon: React.FC<any>
  label: string
  initiallyOpened?: boolean
  links?: { label: string; link: string; Icon?: React.FC<any>; title: string; ariaLabel: string }[]
  link?: string
  title: string
  ariaLabel: string
  func?: () => void | undefined
}

export function LinksGroup({
  icon: Icon,
  label,
  initiallyOpened,
  links,
  link,
  ariaLabel,
  title,
  func
}: LinksGroupProps) {
  const hasLinks = Array.isArray(links)
  const [opened, setOpened] = useState(initiallyOpened || false)
  const location = useLocation()
  const navigate = useNavigate()
  const currentPath = location.pathname
  const collapseId = useId() // unique ID for aria-controls
  const submenuRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    // Reset refs on render
    submenuRefs.current = submenuRefs.current.slice(0, links?.length || 0)
  }, [links])
  
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      const next = submenuRefs.current[index + 1] || submenuRefs.current[0]
      next?.focus()
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      const prev = submenuRefs.current[index - 1] || submenuRefs.current[submenuRefs.current.length - 1]
      prev?.focus()
    }
  }
  const items = (hasLinks ? links : []).map((linkItem, index) => (
    <UnstyledButton
      key={linkItem.label}
      w="95%"
      className={`subLink link ${linkItem.link === currentPath ? "active" : ""}`}
      aria-label={linkItem.ariaLabel}
      title={linkItem.title}
      onClick={() => navigate(linkItem.link)}
      role="menuitem"
      ref={(el) => (submenuRefs.current[index] = el)}
      onKeyDown={(e) => handleKeyDown(e, index)}
    >
      {linkItem.Icon && (
        <ThemeIcon variant="filled" size="lg" radius="xl" color="#5b5eff">
          <linkItem.Icon size={16} />
        </ThemeIcon>
      )}
      {linkItem.label}
    </UnstyledButton>
  ))

  return (
    <div role="menu" aria-label={ariaLabel}>
      <UnstyledButton
        onClick={() => {
          func?.()
          if (link) {
            navigate(link)
            return
          }
          setOpened((o) => !o)
        }}
        className={`control ${currentPath.includes("/issues/") && link?.includes(PATHS?.AUTOMATED_AUDIT_SELECT) || currentPath === link ? "active" : ""}`}
        title={title}
        aria-label={ariaLabel}
        aria-expanded={hasLinks ? opened : undefined}
        aria-controls={hasLinks ? collapseId : undefined}
      >
        <Group w="100%" style={{ justifyContent: "space-between" }}>
          <Box style={{ display: "flex", alignItems: "center" }}>
            <ThemeIcon variant="filled" size="lg" radius="xl" color="#5b5eff">
              <Icon size={18} />
            </ThemeIcon>
            <Box ml="md">{label}</Box>
          </Box>
          {hasLinks && <ChevronRight className={`chevron ${opened ? "rotate" : ""}`} size={16} />}
        </Group>
      </UnstyledButton>

      {hasLinks && (
        <Collapse in={opened} id={collapseId} transitionTimingFunction="linear" role="group" aria-hidden={!opened}>
          {items}
        </Collapse>
      )}
    </div>
  )
}
